import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getChosung, matchKorean } from '../../../../utils/methods';

// 판매등록 모달의 수강권 패널과 같은 정렬 — ㄱㄴㄷ → 영어 → 숫자 → 특문, 같은 무리 안에서는 가나다.
const charOrder = c => {
  if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(c)) return 0;
  if (/[a-zA-Z]/.test(c)) return 1;
  if (/[0-9]/.test(c)) return 2;
  return 3;
};

/**
 * 수강권 선택 드롭다운 — 판매등록 모달 수강권 패널의 축약판(검색 + ㄱㄴㄷ 구분 헤더).
 * options: [{ value, label, hint? }]. 패널은 필드 아래에 떠서(absolute) 주변 레이아웃을 밀지 않는다.
 *
 * multiple 이면 value 는 배열이고 체크박스로 여러 개를 고른다(검색 결과 전체 선택 포함).
 */
const TicketPickerDropdown = ({ value, onChange, options = [], placeholder = '수강권을 선택해 주세요', width = '100%', disabled = false, multiple = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);

  // 바깥 클릭으로 닫기. ESC 는 루트 onKeyDown 에서 잡아 바깥 Modal 까지 올라가지 않게 한다.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const onKeyDown = e => {
    if (open && e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
  };

  // 정렬·ㄱㄴㄷ 헤더는 sortLabel(없으면 label) 기준 — "[일일권]" 같은 접두사가 붙어도 상품명으로 묶인다.
  const sortKey = o => o.sortLabel || o.label || '';
  const sorted = useMemo(() => {
    const q = search.trim();
    const list = q ? options.filter(o => matchKorean(o.label || '', q)) : [...options];
    return list.sort((a, b) => {
      const d = charOrder(sortKey(a)[0] || '') - charOrder(sortKey(b)[0] || '');
      return d !== 0 ? d : sortKey(a).localeCompare(sortKey(b), 'ko');
    });
  }, [options, search]);

  const selectedValues = multiple ? (value || []).map(String) : [];
  const selected = multiple ? null : options.find(o => String(o.value) === String(value)) || null;
  const selectedOptions = multiple ? options.filter(o => selectedValues.includes(String(o.value))) : [];
  const isChecked = o => (multiple ? selectedValues.includes(String(o.value)) : String(o.value) === String(value));
  const allSearchedChecked = multiple && sorted.length > 0 && sorted.every(o => isChecked(o));
  const pick = o => {
    if (!multiple) { onChange(o.value); setOpen(false); setSearch(''); return; }
    const next = selectedValues.includes(String(o.value))
      ? (value || []).filter(v => String(v) !== String(o.value))
      : [...(value || []), o.value];
    onChange(next);
  };
  // 검색 결과 전체 선택/해제 — 검색 없이 열면 곧 전체 수강권이다.
  const toggleAllSearched = () => {
    const searchedValues = sorted.map(o => o.value);
    if (allSearchedChecked) {
      onChange((value || []).filter(v => !searchedValues.some(sv => String(sv) === String(v))));
      return;
    }
    const merged = [...(value || [])];
    searchedValues.forEach(sv => { if (!merged.some(v => String(v) === String(sv))) merged.push(sv); });
    onChange(merged);
  };
  const summary = multiple
    ? (selectedOptions.length === 0 ? placeholder
      : selectedOptions.length === 1 ? selectedOptions[0].label
        : `${selectedOptions[0].label} 외 ${selectedOptions.length - 1}개`)
    : (selected ? selected.label : placeholder);
  const hasValue = multiple ? selectedOptions.length > 0 : !!selected;

  return (
    <Box ref={rootRef} onKeyDown={onKeyDown} sx={{ position: 'relative', width, minWidth: 0 }}>
      <Box
        onClick={() => { if (disabled) return; setOpen(prev => !prev); setSearch(''); }}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '38px', px: '12px',
          border: open ? '1.5px solid #0F7F85' : '1px solid #c4c4c4', borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer', backgroundColor: hasValue ? '#f0f8f8' : '#fff', opacity: disabled ? 0.5 : 1,
          transition: 'all 0.15s ease', '&:hover': disabled ? {} : { borderColor: '#0F7F85' },
        }}>
        <Typography sx={{ fontSize: '13.5px', fontWeight: hasValue ? 500 : 400, color: hasValue ? '#0F7F85' : '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {summary}
        </Typography>
        {open ? <KeyboardArrowUpIcon sx={{ color: '#0F7F85', fontSize: 18, flexShrink: 0 }} /> : <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18, flexShrink: 0 }} />}
      </Box>

      {open && (
        <Box sx={{
          position: 'absolute', top: '42px', left: 0, right: 0, zIndex: 20, backgroundColor: '#fff',
          border: '1.5px solid #0F7F85', borderRadius: '6px', boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
          maxHeight: '360px', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <Box sx={{ p: '8px 10px', borderBottom: '1px solid #eee' }}>
            <TextField
              size="small" autoFocus placeholder="수강권 검색 (초성 검색 가능)" value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '34px', fontSize: '13px' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment> }}
            />
          </Box>
          {multiple && (
            <Box onClick={toggleAllSearched}
              sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '12px', py: '6px', borderBottom: '1px solid #eee', cursor: 'pointer', '&:hover': { backgroundColor: '#f8f8f8' } }}>
              <Checkbox size="small" checked={allSearchedChecked} sx={{ p: 0, color: '#c4c4c4', '&.Mui-checked': { color: '#0F7F85' } }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>
                {search.trim() ? `검색 결과 ${sorted.length}개 전체 선택` : '전체 선택'}
              </Typography>
              {selectedValues.length > 0 && (
                <Typography sx={{ ml: 'auto', fontSize: '12px', color: '#0F7F85', fontWeight: 700 }}>{selectedValues.length}개 선택</Typography>
              )}
            </Box>
          )}
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {sorted.length === 0 ? (
              <Typography sx={{ p: '20px', textAlign: 'center', fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
            ) : (() => {
              let lastCho = '';
              return sorted.map(o => {
                const cho = getChosung(sortKey(o)[0] || '');
                const showHeader = cho !== lastCho;
                lastCho = cho;
                const isSelected = isChecked(o);
                return (
                  <React.Fragment key={o.value}>
                    {showHeader && (
                      <Box sx={{ px: '12px', py: '3px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #eee' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>{cho}</Typography>
                      </Box>
                    )}
                    <Box
                      onClick={() => pick(o)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '12px', py: '8px', cursor: 'pointer',
                        backgroundColor: isSelected ? '#f0f8f8' : '#fff', borderLeft: isSelected ? '3px solid #0F7F85' : '3px solid transparent',
                        '&:hover': { backgroundColor: isSelected ? '#e8f5f5' : '#f8f8f8' },
                      }}>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13.5px', color: isSelected ? '#0F7F85' : '#333', fontWeight: isSelected ? 500 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {multiple && <Checkbox size="small" checked={isSelected} sx={{ p: 0, color: '#c4c4c4', '&.Mui-checked': { color: '#0F7F85' } }} />}
                        {o.label}
                        {o.hint && <span style={{ fontSize: 12, color: '#999', marginLeft: 6 }}>{o.hint}</span>}
                      </Typography>
                      {!multiple && isSelected && <CheckCircleIcon sx={{ fontSize: 16, color: '#0F7F85', flexShrink: 0 }} />}
                    </Box>
                  </React.Fragment>
                );
              });
            })()}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TicketPickerDropdown;
